import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cn, Flex, Text } from '@kognitos/lattice';

import { StatusDot, type TStatusDotTone } from '@/shared/components';

import type { TTriggerStatus } from './TriggerType';

const STATUS_TONE: Record<TTriggerStatus, TStatusDotTone> = {
  active: 'success',
  error: 'destructive',
  paused: 'muted',
  pending: 'info',
};

/**
 * Props for {@link TriggerRow}.
 */
export interface ITriggerRowProps {
  icon: ReactNode;
  /** The trigger's kind, above the title, e.g. "Email". */
  typeLabel: string;
  /** What identifies this trigger, e.g. its mailbox address. */
  title: string;
  status: TTriggerStatus;
  /** Opens the trigger's configuration. */
  onOpen: () => void;
  /** The row's actions menu; omit for a read-only row. */
  menu?: ReactNode;
  'data-testid'?: string;
}

/**
 * One trigger on a configuration rail: its type, what identifies it, a status
 * dot, and an optional actions menu. Shared by the automation and agent rails.
 */
export function TriggerRow({
  icon,
  typeLabel,
  title,
  status,
  onOpen,
  menu,
  'data-testid': dataTestId = 'automation-trigger-row',
}: ITriggerRowProps) {
  const { t } = useTranslation('automation-inputs');

  return (
    <Flex
      align="center"
      className={cn(
        'h-[70px] min-w-0 w-full max-w-full overflow-hidden rounded-xl border bg-card p-1 transition-colors hover:bg-accent',
        status === 'error' ? 'border-destructive/50' : 'border-border',
      )}
      data-testid={dataTestId}
    >
      <Button
        type="button"
        variant="ghost"
        onClick={onOpen}
        className="h-auto min-w-0 flex-1 justify-start gap-2 px-2 py-2 text-left !bg-transparent hover:!bg-transparent"
      >
        <Flex
          align="center"
          justify="center"
          className="size-9 shrink-0 rounded-lg bg-muted"
        >
          {icon}
        </Flex>
        <Flex direction="column" gap={0} className="min-w-0 flex-1">
          <Text
            level="xSmall"
            color="muted"
            weight="normal"
            className="w-full truncate"
          >
            {typeLabel}
          </Text>
          <Text
            level="small"
            weight="normal"
            className="w-full truncate"
            title={title}
          >
            {title}
          </Text>
        </Flex>
        <Flex align="center" className="shrink-0">
          <StatusDot tone={STATUS_TONE[status]} size="md" />
          <Text
            level="small"
            color={status === 'paused' ? 'muted' : undefined}
            className="sr-only"
          >
            {t(`triggerTypes.status.${status}`)}
          </Text>
        </Flex>
      </Button>
      {menu}
    </Flex>
  );
}
